
import { apiService } from './apiService';
import { API_CONFIG } from '../config/apiConfig';
import { Customer } from '@/types';
import { toast } from '@/hooks/use-toast';
import { generateId } from './storageService';

interface ApiCustomer {
  ID?: number;
  Nome?: string;
  Documento?: string;
  CPFCNPJ?: string;
  Telefone?: string;
  Email?: string;
  Endereco?: string;
  Logradouro?: string;
  Bairro?: string;
  Cidade?: string;
  UF?: string;
  Cep?: string;
  ClienteID?: number;
  Limite?: number;
  TipoContribuinte?: number;
  ConsumidorFinal?: boolean;
  // Outros campos que possam existir
}

const mapApiCustomerToCustomer = (apiCustomer: ApiCustomer): Customer => {
  return {
    id: String(apiCustomer.ID || apiCustomer.ClienteID || '0'),
    name: apiCustomer.Nome || '',
    document: apiCustomer.CPFCNPJ || apiCustomer.Documento || '',
    phone: apiCustomer.Telefone || '',
    email: apiCustomer.Email || '',
    address: apiCustomer.Logradouro || apiCustomer.Endereco || '',
    city: apiCustomer.Cidade || '',
    state: apiCustomer.UF || '',
    zipCode: apiCustomer.Cep || '',
    clienteID: apiCustomer.ClienteID || apiCustomer.ID || 0
  };
};

export const fetchCustomersFromApi = async (): Promise<Customer[]> => {
  try {
    // Inicializa a configuração da API
    API_CONFIG.initialize();
    
    if (!API_CONFIG.baseURL) {
      console.log('API não configurada para clientes');
      return getMockCustomers();
    }

    console.log('Buscando clientes da API');
    // Usando o endpoint correto para clientes
    const url = '/api/PessoaClientesApi';
    const response = await apiService.get<ApiCustomer[]>(url);
    
    if (!response || !Array.isArray(response)) {
      console.warn('Formato de resposta inválido para clientes:', response);
      return getMockCustomers();
    }
    
    const mappedCustomers = response.map(apiCustomer => mapApiCustomerToCustomer(apiCustomer));
    
    console.log(`Mapeados ${mappedCustomers.length} clientes da API`);
    return mappedCustomers;
  } catch (error) {
    console.error('Erro ao buscar clientes:', error);
    return getMockCustomers();
  }
};

export const fetchCustomerByIdFromApi = async (id: string): Promise<Customer | null> => {
  try {
    if (!API_CONFIG.baseURL) {
      return null;
    }

    const url = `/api/PessoaClientesApi/${id}`;
    const response = await apiService.get<ApiCustomer>(url);
    
    if (!response) {
      return null;
    }
    
    return mapApiCustomerToCustomer(response);
  } catch (error) {
    console.error(`Erro ao buscar cliente com ID ${id}:`, error);
    return null;
  }
};

export const syncCustomersWithApi = async (): Promise<boolean> => {
  try {
    // Inicializa a configuração da API
    API_CONFIG.initialize();
    
    const customers = await fetchCustomersFromApi();
    
    if (customers && customers.length > 0) {
      localStorage.setItem('restaurant_customers', JSON.stringify(customers));
      
      toast({
        title: 'Clientes sincronizados',
        description: `${customers.length} clientes foram sincronizados com sucesso`,
      });
      
      return true;
    }
    
    console.log('Nenhum cliente encontrado para sincronização');
    return false;
  } catch (error) {
    console.error('Erro ao sincronizar clientes:', error);
    return false;
  }
};

// Validar CPF
export const validateCPF = (cpf: string): boolean => {
  // Remove caracteres não numéricos
  cpf = cpf.replace(/[^\d]/g, '');
  
  // Verifica se tem 11 dígitos
  if (cpf.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cpf)) return false;
  
  // Validação do primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let resto = soma % 11;
  let digitoVerificador1 = resto < 2 ? 0 : 11 - resto;
  
  if (digitoVerificador1 !== parseInt(cpf.charAt(9))) return false;
  
  // Validação do segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i)) * (11 - i);
  }
  resto = soma % 11;
  let digitoVerificador2 = resto < 2 ? 0 : 11 - resto;
  
  if (digitoVerificador2 !== parseInt(cpf.charAt(10))) return false;
  
  return true;
};

// Validar CNPJ
export const validateCNPJ = (cnpj: string): boolean => {
  // Remove caracteres não numéricos
  cnpj = cnpj.replace(/[^\d]/g, '');
  
  // Verifica se tem 14 dígitos
  if (cnpj.length !== 14) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cnpj)) return false;
  
  // Validação do primeiro dígito verificador
  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  const digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado !== parseInt(digitos.charAt(0))) return false;
  
  // Validação do segundo dígito verificador
  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado !== parseInt(digitos.charAt(1))) return false;
  
  return true;
};

// Formatar CPF
export const formatCPF = (cpf: string): string => {
  cpf = cpf.replace(/[^\d]/g, '');
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

// Formatar CNPJ
export const formatCNPJ = (cnpj: string): string => {
  cnpj = cnpj.replace(/[^\d]/g, '');
  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
};

// Formatar documento (CPF ou CNPJ)
export const formatDocument = (doc: string): string => {
  doc = doc.replace(/[^\d]/g, '');
  if (doc.length <= 11) {
    return formatCPF(doc);
  } else {
    return formatCNPJ(doc);
  }
};

// Validar documento (CPF ou CNPJ)
export const validateDocument = (doc: string): boolean => {
  doc = doc.replace(/[^\d]/g, '');
  if (doc.length === 11) {
    return validateCPF(doc);
  } else if (doc.length === 14) {
    return validateCNPJ(doc);
  }
  return false;
};

const getMockCustomers = (): Customer[] => {
  return [
    { 
      id: '1', 
      name: 'Cliente Padrão', 
      document: '000.000.000-00',
      phone: '(00) 00000-0000' 
    },
    { 
      id: '2', 
      name: 'Maria Silva', 
      document: '123.456.789-00',
      phone: '(11) 98765-4321',
      email: 'maria@email.com' 
    },
    { 
      id: '3', 
      name: 'João Oliveira', 
      document: '987.654.321-00',
      phone: '(11) 91234-5678',
      email: 'joao@email.com' 
    }
  ];
};
